import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, mergeProps as _mergeProps, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "ellipsis" }

import { QSelect } from 'quasar';
import { computed, onMounted, ref, useAttrs, useSlots, watch } from 'vue';

import SCheckbox from 'SharedComponents/ui/Checkbox/SCheckbox.vue';
import SIcon from 'SharedComponents/ui/Icon/SIcon.vue';
import SIconTooltiped from 'SharedComponents/ui/Icon/SIconTooltiped.vue';
import { useLazyLoadOptions } from 'SharedComponents/ui/Select/useLazyLoadOptions';
import { PaginationParams } from 'SharedDir/declarations/pagination';

export type SSelectProps = {
  required?: boolean;
  indent?: boolean;
  lazy?: boolean;
  /**
   * Подсказка при исопльзовании поиска на беке
   */
  lazyHint?: string;
  filtering?: boolean;
  hideHint?: boolean;
  emitValue?: boolean;
  mapValues?: boolean;
  options?: unknown[];
  factory?: <V, T extends object>(
    params: PaginationParams<T & { filter: string }>
  ) => Promise<Array<V>>;
  filterTreshold?: number;
  collapsible?: boolean;
  resolveValue?: <T>(value: unknown) => Promise<Array<T>>;
};


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'SSelect', components: { SCheckbox } },
  __name: 'SSelect',
  props: {
    required: { type: Boolean, default: false },
    indent: { type: Boolean, default: false },
    lazy: { type: Boolean, default: false },
    lazyHint: { default: 'Пожалуйста, введите поисковую фразу' },
    filtering: { type: Boolean, default: false },
    hideHint: { type: Boolean, default: false },
    emitValue: { type: Boolean, default: true },
    mapValues: { type: Boolean, default: true },
    options: { default: () => [] as unknown[] },
    factory: {},
    filterTreshold: { default: 3 },
    collapsible: { type: Boolean, default: false },
    resolveValue: {}
  },
  setup(__props: any, { expose: __expose }) {



const attrs = useAttrs();
const slots = useSlots();

const props = __props;

const select = ref<QSelect>(null as unknown as QSelect);

const {
  smartOptions,
  smartUseInput,
  smartLoading,
  smartFilter,
  smartVirtualScroll,
  smartBlur,
  lazyHint,
  lazyResolver,
} = useLazyLoadOptions(attrs, props, select);

const filteredOptions = ref<unknown[]>(props.options);

// Если при инициализации приложения ещё не пришли options
watch(
  () => props.options,
  (newVal, oldVal) => {
    if (filteredOptions.value.length === 0) {
      filteredOptions.value = newVal;
    }
  }
);

watch(
  () => attrs.modelValue,
  (newVal, oldVal) => {
    if (props.lazy && props.resolveValue) {
      lazyResolver(newVal);
    }
  }
);

const simpleFilter = (filter, done) => {
  if (filter) {
    filteredOptions.value = props.options.filter((opt) =>
      select.value.getOptionLabel(opt).toLowerCase().includes(filter.toLowerCase())
    );
  } else {
    filteredOptions.value = props.options;
  }
  done();
};

const filter = computed(() => (props.filtering ? simpleFilter : smartFilter.value));

const classes = computed(() => {
  let result = 's-select ';
  result += `${props.required ? 's-select--required' : ''} `;
  result += `${attrs.readonly ? 's-select--readonly' : ''} `;
  result += `${attrs.disable ? 's-select--disable' : ''} `;
  result += `${typeof attrs.dense !== 'undefined' ? 's-select--dense' : ''} `;
  result += `${props.indent ? 's-input--indent' : ''} `;

  return result;
});

const chip = computed(() => {
  return typeof attrs.multiple !== 'undefined';
});

const removable = computed(() => typeof attrs.clearable !== 'undefined');

const readonly = computed(() => typeof attrs.readonly !== 'undefined');

const withCollaps = computed(() => chip.value && props.collapsible);

const withOptionsLabel = computed(() => typeof attrs['option-label'] !== 'undefined');

const width = ref(500);

const computedOptions = computed(() =>
  props.filtering ? filteredOptions.value : smartOptions.value
);

const getModelValueLength = computed(() => {
  if (!Array.isArray(attrs.modelValue)) {
    return 0;
  }

  return attrs.modelValue.length;
});

function getCollapseChipLabel(opt) {
  if (withOptionsLabel.value) {
    return opt[String(attrs.optionLabel)];
  }
  return opt;
}

function removeFirstElement() {
  if (!Array.isArray(attrs.modelValue)) return;

  attrs.modelValue.splice(0, 1);
}

function widthSelect(): void {
  const newWidth = select.value.$el.clientWidth - 6;
  if (newWidth > 250) {
    width.value = newWidth;
  } else {
    width.value = 250;
  }
}

function resetValidation(): void {
  select.value.resetValidation();
}

function blur(): void {
  select.value.blur();
}
function hidePopup(): void {
  select.value.hidePopup();
}

__expose({
  hidePopup,
  blur,
});

// При открытии нужно пересчитать ее максимальную ширину.
function popupShow(): void {
  widthSelect();
}

onMounted(() => {
  widthSelect();
  if (props.lazy && props.resolveValue) {
    lazyResolver(attrs.modelValue);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_chip = _resolveComponent("q-chip")!

  return (_openBlock(), _createBlock(_unref(QSelect), {
    ref_key: "select",
    ref: select,
    class: _normalizeClass(classes.value),
    "use-chips": chip.value,
    "popup-content-class": "s-select__items",
    outlined: "",
    "options-dense": "",
    "emit-value": _ctx.emitValue,
    "map-options": _ctx.mapValues,
    "hide-bottom-space": "",
    "clear-icon": "app:delete",
    "dropdown-icon": "app:arrow-light-bottom",
    options: computedOptions.value,
    "use-input": _ctx.filtering || _unref(smartUseInput),
    loading: _unref(attrs).loading || _unref(smartLoading),
    hint: _ctx.hideHint ? undefined : _unref(lazyHint),
    onFilter: filter.value,
    onVirtualScroll: _unref(smartVirtualScroll),
    onBlur: _unref(smartBlur),
    onPopupShow: popupShow
  }, _createSlots({
    option: _withCtx((scope) => [
      _createVNode(_component_q_item, _mergeProps(scope.itemProps, {
        style: `box-sizing: border-box; max-width: ${width.value}px;`,
        class: { 's-select__menu-active': scope.selected }
      }), {
        default: _withCtx(() => [
          (chip.value)
            ? (_openBlock(), _createBlock(_component_q_item_section, {
                key: 0,
                avatar: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(SCheckbox, {
                    "model-value": scope.selected,
                    "onUpdate:modelValue": ($event: any) => (scope.toggleOption(scope.opt))
                  }, null, 8, ["model-value", "onUpdate:modelValue"])
                ]),
                _: 2
              }, 1024))
            : _createCommentVNode("", true),
          (scope.opt)
            ? _renderSlot(_ctx.$slots, "option-prepend", _normalizeProps(_mergeProps({ key: 1 }, scope.opt)))
            : _createCommentVNode("", true),
          _createVNode(_component_q_item_section, null, {
            default: _withCtx(() => [
              _createVNode(_component_q_item_label, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(scope.label), 1)
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1024),
          (scope.opt)
            ? _renderSlot(_ctx.$slots, "option-append", _normalizeProps(_mergeProps({ key: 2 }, scope.opt)))
            : _createCommentVNode("", true)
        ]),
        _: 2
      }, 1040, ["style", "class"])
    ]),
    "no-option": _withCtx(() => [
      _createVNode(_component_q_item, null, {
        default: _withCtx(() => [
          _createVNode(_component_q_item_section, { class: "text-grey" }, {
            default: _withCtx(() => _cache[0] || (_cache[0] = [
              _createTextVNode(" Ничего не найдено")
            ])),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _renderSlot(_ctx.$slots, "default")
    ]),
    _: 2
  }, [
    (_ctx.lazy && !_ctx.$attrs.readonly)
      ? {
          name: "prepend",
          fn: _withCtx(() => [
            _createVNode(SIconTooltiped, {
              name: "app:search",
              tooltip: "Для отображения вариантов выбора,начните вводить поисковый запрос"
            })
          ]),
          key: "0"
        }
      : undefined,
    (withCollaps.value)
      ? {
          name: "selected-item",
          fn: _withCtx((el) => [
            (el.index === 0)
              ? (_openBlock(), _createBlock(_component_q_chip, {
                  key: 0,
                  removable: removable.value && !_ctx.$attrs.readonly && !_ctx.$attrs.disable,
                  class: "q-chip--dense",
                  onRemove: removeFirstElement
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", _hoisted_1, _toDisplayString(getCollapseChipLabel(el.opt)), 1)
                  ]),
                  _: 2
                }, 1032, ["removable"]))
              : _createCommentVNode("", true),
            (el.index === 1)
              ? (_openBlock(), _createBlock(_component_q_chip, {
                  key: 1,
                  class: "q-chip--dense collapse"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" +" + _toDisplayString(getModelValueLength.value - 1), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          key: "1"
        }
      : undefined,
    _renderList(_ctx.$slots, (_, name) => {
      return {
        name: name,
        fn: _withCtx((slotData) => [
          _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(slotData || {})))
        ])
      }
    })
  ]), 1032, ["class", "use-chips", "emit-value", "map-options", "options", "use-input", "loading", "hint", "onFilter", "onVirtualScroll", "onBlur"]))
}
}

})